/**
 * Canonical identity permission strings, copied verbatim from
 * openapi/generated/route_policy.gen.go.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { useSession } from "@/lib/session/use-session";

export const IDENTITY_PERMISSIONS = {
  userRead: "platform.user.read",
  userWrite: "platform.user.write",
  userPassword: "platform.user.password.set",
  roleRead: "platform.role.read",
  roleWrite: "platform.role.write",
  permissionRead: "platform.permission.read",
} as const;

export type IdentityPermission = (typeof IDENTITY_PERMISSIONS)[keyof typeof IDENTITY_PERMISSIONS];

/** Direct-route authorization: a denied page never mounts or issues queries. */
export function IdentityPermissionBoundary({
  permission,
  children,
}: {
  permission: IdentityPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card role="alert" className="w-full max-w-xl border-destructive/30">
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <h1 className="text-xl font-semibold tracking-tight">
              {t("identity.permissions.title")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
